import { useDispatch, useStore } from "react-redux";

import type { RootState } from "@/store";
import {
  recalculateWeightPercentages,
  setWeightValuesForDatasetId
} from "@/store/slice/datasetSlice";

import type { AnalysisConfiguration } from "@lokasi-ai-web-sdk/agent-ui";

import { toWeightPatches } from "./utils";

/**
 * Applies the weights the agent set to the app's weight model.
 *
 * Re-asserted on every sync instead of being gated on a ledger, because adding
 * or removing any dataset empties `weightValues` and rebuilds it from defaults
 * (`recalculateWeightValuesPreservingZScores`, then `initializeWeightValues`).
 * The agent's numbers therefore have to be written again after one — and
 * writing them again is what makes this idempotent, with no membership
 * bookkeeping of its own.
 *
 * Ownership lives in `toWeightPatches`: only ledgered datasets the agent
 * actually weighted are ever touched. Nothing else on this page can empty the
 * map behind the sync's back either — the map's dataset panel deliberately
 * offers no row actions.
 */
export const useAgentDatasetWeights = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  const syncDatasetWeights = (
    configuration: AnalysisConfiguration | null,
    ledger: ReadonlyMap<string, { compositeId: string }>
  ) => {
    const { datasets, weightValues } = store.getState().dataset;

    const patches = toWeightPatches(
      configuration?.datasets,
      ledger,
      datasets,
      weightValues
    );

    if (patches.length === 0) return;

    for (const patch of patches) {
      dispatch(setWeightValuesForDatasetId(patch));
    }

    // Percentages are derived from the weights, and this is the one reducer
    // that recomputes them without resetting the weights it read.
    dispatch(recalculateWeightPercentages());
  };

  return { syncDatasetWeights };
};
